// export const serverIP = "https://ticket-system-server-kzww.vercel.app/";

// Auto-detect HTTPS based on current window location for PWA compatibility
const getServerIP = () => {
  // If running on HTTPS (required for PWA), use HTTPS backend
  if (window.location.protocol === 'https:') {
    return process.env.REACT_APP_SERVER_IP || "https://ticket.sunvalleycyprus.com/";
  }
  // Otherwise use HTTP for development
  return process.env.REACT_APP_SERVER_IP || "http://192.168.0.109:5000/";
};

export const serverIP = getServerIP();

// Simple in-memory cache for GET requests
const apiCache = new Map();
const CACHE_TTL = 5000; // 5 seconds

const getCachedData = (key) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  apiCache.set(key, { data, timestamp: Date.now() });
};

export const fetchTicketDetails = async (ticketId, token) => {
  try {
    const cacheKey = `ticket_${ticketId}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await fetch(`${serverIP}api/tickets/${ticketId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching ticket details: ${response.statusText}`);
    }

    const data = await response.json();
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    console.error("Error fetching ticket details:", error);
    throw error;
  }
};

// Mark message as seen by current user
export const markMessageAsSeen = async (ticketId, messageId, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/messages/${messageId}/seen`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error marking message as seen: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error marking message as seen:", error);
    throw error;
  }
};

// Fetch who has seen a specific message
export const fetchMessageSeenBy = async (ticketId, messageId, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/messages/${messageId}/seen`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching message seen by: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching message seen by:", error);
    throw error;
  }
};

// Fetch seen status for all messages in a ticket
export const fetchTicketMessagesSeenStatus = async (ticketId, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/messages/seen-status`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching messages seen status: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching messages seen status:", error);
    throw error;
  }
};

// Mark multiple messages as seen (bulk operation)
export const markMultipleMessagesAsSeen = async (ticketId, messageIds, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/messages/mark-seen`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ messageIds }),
    });

    if (!response.ok) {
      throw new Error(`Error marking messages as seen: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error marking messages as seen:", error);
    throw error;
  }
};

// Get unseen message count for current user
export const getUnseenMessageCount = async (ticketId, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/unseen-count`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching unseen count: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching unseen count:", error);
    throw error;
  }
};

// Get unseen message counts for multiple tickets (bulk operation)
export const getBulkUnseenMessageCounts = async (ticketIds, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/unseen-counts`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ticketIds }),
    });

    if (!response.ok) {
      throw new Error(`Error fetching bulk unseen counts: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching bulk unseen counts:", error);
    // Return empty object on error so the UI doesn't break
    return {};
  }
};


export const fetchTicketMessages = async (ticketId, token) => {
  try {
    const response = await fetch(
      `${serverIP}api/tickets/${ticketId}/messages`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error fetching messages: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
};

export const sendMessage = async (ticketId, messageObject, token, files = null) => {
  try {
    let body;
    let headers = {
      Authorization: `Bearer ${token}`,
    };

    if (files && files.length > 0) {
      // Use FormData for file uploads
      const formData = new FormData();
      formData.append('message', messageObject.message);
      formData.append('sender', messageObject.sender);
      formData.append('sender_name', messageObject.sender_name);
      formData.append('sender_surname', messageObject.sender_surname);

      // Add files to FormData
      files.forEach((file) => {
        formData.append('files', file);
      });

      body = formData;
      // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
    } else {
      // Use JSON for text-only messages
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(messageObject);
    }

    const response = await fetch(
      `${serverIP}api/tickets/${ticketId}/messages`,
      {
        method: "POST",
        headers,
        body,
      }
    );

    if (!response.ok) {
      throw new Error(`Error sending message: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};

export const fetchTreeData = async (token) => {
  try {
    const response = await fetch(`${serverIP}api/tree-data`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch tree data");
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching tree data:", error);
    throw error;
  }
};

export const fetchCountsTree = async (token) => {
  try {
    const cacheKey = `counts_${token.substring(token.length - 10)}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await fetch(`${serverIP}api/counts`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch counts");
    }
    const data = await response.json();
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    console.error("Error fetching counts:", error);
    throw error;
  }
};

export const fetchDepartments = async (token) => {
  try {
    const response = await fetch(`${serverIP}api/departments`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch departments");
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to fetch departments", error);
    throw error;
  }
};

export const createDepartment = async (name, description, token) => {
  try {
    const response = await fetch(`${serverIP}api/departments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name, description }),
    });
    if (!response.ok) {
      throw new Error("Failed to create department");
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to create department", error);
    throw error;
  }
};

export const updateDepartment = async (id, name, description, token) => {
  try {
    const response = await fetch(`${serverIP}api/departments/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name, description }),
    });
    if (!response.ok) {
      throw new Error("Failed to update department");
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to update department", error);
    throw error;
  }
};

export const deleteDepartment = async (id, token) => {
  try {
    const response = await fetch(`${serverIP}api/departments/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Failed to delete department");
    }
  } catch (error) {
    console.error("Failed to delete department", error);
    throw error;
  }
};

export const handleForwardToUsers = async (
  ticketId,
  userIds,
  token,
  setLoading
) => {
  if (!ticketId || !userIds.length) {
    alert("Please select a ticket and at least one user");
    return;
  }

  setLoading(true);

  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/forward`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ recipients: userIds }),
    });

    if (response.ok) {
      alert("Ticket forwarded successfully");
    } else {
      const errorData = await response.text();
      console.error("Failed to forward ticket:", response.status, errorData);
      alert("Failed to forward ticket");
    }
  } catch (error) {
    console.error("Error forwarding ticket", error);
    alert("Failed to forward ticket");
  } finally {
    setLoading(false);
  }
};

// Fetch all subjects or subjects by department
export const fetchSubjects = async (token, departmentId = null, reqHeaders = null) => {
  try {
    let url = `${serverIP}api/subjects`;
    if (departmentId) {
      url += `?departmentId=${departmentId}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-user-role": reqHeaders?.role || "",
        "x-user-department": reqHeaders?.department || "",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch subjects");
    }

    return await response.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Create a new subject
export const createSubject = async (name, departmentId, token, reqHeaders = null) => {
  try {
    const response = await fetch(`${serverIP}api/subjects`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-user-role": reqHeaders?.role || "",
        "x-user-department": reqHeaders?.department || "",
      },
      body: JSON.stringify({ name, departmentId }),
    });

    if (!response.ok) {
      throw new Error("Failed to create subject");
    }

    return await response.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Update a subject by ID
export const updateSubject = async (id, name, token, reqHeaders = null) => {
  try {
    const response = await fetch(`${serverIP}api/subjects/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-user-role": reqHeaders?.role || "",
        "x-user-department": reqHeaders?.department || "",
      },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      throw new Error("Failed to update subject");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to update subject", error);
    throw error;
  }
};

// Delete a subject by ID
export const deleteSubject = async (id, token, reqHeaders = null) => {
  try {
    const response = await fetch(`${serverIP}api/subjects/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-user-role": reqHeaders?.role || "",
        "x-user-department": reqHeaders?.department || "",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to delete subject");
    }

    // Optionally, return a success message
    return "Subject deleted successfully";
  } catch (error) {
    console.error(error);
    throw error; // Rethrow the error to handle it in the calling function
  }
};

// Update ticket status
export const updateTicketStatus = async (ticketId, status, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/status`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw new Error("Failed to update ticket status");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating ticket status:", error);
    throw error;
  }
};

// Collaboration APIs
export const fetchTicketCollaborators = async (ticketId, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/collaborators`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching collaborators: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching collaborators:", error);
    throw error;
  }
};

export const addTicketCollaborator = async (ticketId, departmentId, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/collaborators`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ departmentId }),
    });

    if (!response.ok) {
      throw new Error(`Error adding collaborator: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding collaborator:", error);
    throw error;
  }
};

export const removeTicketCollaborator = async (ticketId, departmentId, token) => {
  try {
    const response = await fetch(`${serverIP}api/tickets/${ticketId}/collaborators/${departmentId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error removing collaborator: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error removing collaborator:", error);
    throw error;
  }
};
